"use client";

import Image from "next/image";
import ImageWrapper from "@/component/ui/ImageWrapper";
import CustomSlider from "@/component/ui/CustomSlider/CustomSlider";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const MODAL_SLIDER_RESPONSIVE = { desktop: 1, tablet: 1, mobile: 1 } as const;

function clampSlideIndex(index: number, imageCount: number) {
    if (imageCount <= 0) return 0;
    return Math.min(Math.max(index, 0), imageCount - 1);
}

type Props = {
    open: boolean;
    data: {
        title: string;
        images: string[];
        imageCount?: number;
    } | null;
    onClose: () => void;
    /** Slide the popup should open on. */
    startIndex?: number;
};

export default function CarImageCardModal({ open, data, onClose, startIndex = 0 }: Props) {
    const [mounted, setMounted] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [sliderReady, setSliderReady] = useState(false);

    const images = data?.images ?? [];
    const imageCount = images.length;

    useEffect(() => {
        setMounted(true);
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            setSliderReady(false);
            return;
        }
        setViewerIndex(clampSlideIndex(startIndex, imageCount));
        setSliderReady(true);
    }, [open, startIndex, imageCount]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    const sliderKey = useMemo(
        () => `car-image-modal-${clampSlideIndex(startIndex, imageCount)}-${imageCount}`,
        [startIndex, imageCount],
    );

    if (!open || !data || !mounted) return null;

    const modal = (
        <div
            className="fixed inset-0 z-[99999] flex w-screen items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-label={data.title}
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/70"
                aria-label="Close gallery"
                onClick={onClose}
            />

            <div className="relative z-10 w-[95%] max-w-5xl bg-black rounded-2xl overflow-hidden">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close gallery"
                    className="absolute top-4 right-4 z-20 rounded-full bg-black/60 p-2 text-white"
                >
                    <Image
                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                        alt="cross icon"
                        title="cross icon"
                        width={16}
                        height={16}
                        className="invert"
                    />
                </button>

                <div className="absolute top-4 left-4 z-20 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                    {viewerIndex + 1} / {imageCount}
                </div>

                <div className="rounded-2xl overflow-hidden m-0 md:m-[-7px]">
                    {sliderReady && (
                        <CustomSlider
                            key={sliderKey}
                            itemsToShow={1}
                            responsive={MODAL_SLIDER_RESPONSIVE}
                            showArrows
                            showDots
                            variant="modal-dots"
                            className="h-auto"
                            activeIndex={viewerIndex}
                            onSlideChange={setViewerIndex}
                        >
                            {images.map((src, index) => (
                                <div key={`${src}-${index}`} className="relative h-[203px] w-full md:h-[576px]">
                                    <ImageWrapper
                                        src={src}
                                        alt={`${data.title} - Image ${index + 1}`}
                                        title={data.title}
                                        fill
                                        sourceWidth={1024}
                                        sourceHeight={576}
                                        className="object-cover"
                                        priority={index === viewerIndex}
                                    />
                                </div>
                            ))}
                        </CustomSlider>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
